import { Head, Link, router } from "@inertiajs/react";
import type { ColumnDef } from "@tanstack/table-core";
import { formatDistanceToNow } from "date-fns";
import { es } from "date-fns/locale";
import {
  Bell,
  Briefcase,
  Building2,
  Check,
  CheckCircle2,
  ChevronRight,
  HelpCircle,
  Inbox,
  Loader2,
} from "lucide-react";

import { HelpButton, HelpDialog } from "@/components/help-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Item,
  ItemContent,
  ItemDescription,
  ItemGroup,
  ItemSeparator,
  ItemTitle,
} from "@/components/ui/item";
import {
  PURCHASE_REQUEST_STATUS_COLORS,
  purchaseRequestStatusLabel,
} from "@/constants/purchase-request";
import { useNotifications } from "@/contexts/notifications-context";
import { useHelpDialog } from "@/hooks/use-help-dialog";
import AppLayout from "@/layouts/app-layout";
import { DataTable } from "@/lib/data-table";
import { DataTableColumnHeader } from "@/lib/data-table-column-header";
import { cn } from "@/lib/utils";
import { notificationsPath, organizationDashboardPath } from "@/routes";
import type {
  BreadcrumbItem,
  GlobalApprovalRow,
  GlobalApprovalsIndexProps,
  GroupedApprovals,
  OrganizationSummary,
  SerializedNotification,
  WorkItemRow,
} from "@/types";
import { formatDateAR } from "@/utils/date";

import { MyWorkItemsSection } from "./components/my-work-items-section";

interface OrganizationsQuickAccessProps {
  organizations: OrganizationSummary[];
}

function OrganizationsQuickAccess({
  organizations,
}: OrganizationsQuickAccessProps) {
  if (organizations.length === 0) return null;

  return (
    <Item variant="outline" className="flex-col items-stretch">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Building2 className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm font-medium">Mis organizaciones</span>
        </div>
        <Badge variant="secondary" className="text-xs">
          {organizations.length}
        </Badge>
      </div>

      <ItemGroup className="mt-2">
        {organizations.map((org, index) => (
          <div key={org.slug}>
            {index > 0 && <ItemSeparator />}
            <Link
              href={organizationDashboardPath(org.slug)}
              className="block"
              prefetch
            >
              <Item size="sm" className="cursor-pointer hover:bg-muted/50">
                <div className="flex items-center gap-3 w-full">
                  {org.logo_url ? (
                    <img
                      src={org.logo_url}
                      alt={org.name}
                      className="h-8 w-8 rounded-md object-cover shrink-0"
                    />
                  ) : (
                    <div className="h-8 w-8 rounded-md bg-primary/10 flex items-center justify-center shrink-0">
                      <Building2 className="h-4 w-4 text-primary" />
                    </div>
                  )}
                  <ItemContent className="min-w-0">
                    <ItemTitle className="truncate">{org.name}</ItemTitle>
                    <ItemDescription className="text-xs">
                      /{org.slug}
                    </ItemDescription>
                  </ItemContent>
                  <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0 ml-auto" />
                </div>
              </Item>
            </Link>
          </div>
        ))}
      </ItemGroup>
    </Item>
  );
}

const approvalColumns: ColumnDef<GlobalApprovalRow>[] = [
  {
    accessorKey: "id",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="ID" />
    ),
    enableSorting: false,
  },
  {
    accessorKey: "sector_name",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Sector" />
    ),
    cell: ({ row }) => row.original.sector_name ?? "-",
    enableSorting: false,
  },
  {
    accessorKey: "status",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Estado" />
    ),
    cell: ({ row }) => {
      const status = row.original.status;
      const color =
        PURCHASE_REQUEST_STATUS_COLORS[
          status as keyof typeof PURCHASE_REQUEST_STATUS_COLORS
        ] ?? "";
      return (
        <Badge className={color}>{purchaseRequestStatusLabel(status)}</Badge>
      );
    },
    enableSorting: false,
  },
  {
    accessorKey: "created_at",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Creado" />
    ),
    cell: ({ row }) => <span>{formatDateAR(row.original.created_at)}</span>,
    enableSorting: false,
  },
  {
    id: "action",
    cell: () => <ChevronRight className="h-4 w-4 text-muted-foreground" />,
    enableSorting: false,
  },
];

const breadcrumbs: BreadcrumbItem[] = [
  { title: "Mi trabajo", href: "/approvals" },
];

interface OrganizationApprovalsProps {
  group: GroupedApprovals;
  onRowClick: (row: GlobalApprovalRow) => void;
}

function OrganizationApprovals({
  group,
  onRowClick,
}: OrganizationApprovalsProps) {
  const urgentCount = group.approvals.filter(
    (a) => a.assigned_to_current_user,
  ).length;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Building2 className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm font-medium">{group.organization.name}</span>
          {urgentCount > 0 && (
            <Badge variant="outline" className="ml-2 text-xs">
              {urgentCount} tu turno
            </Badge>
          )}
        </div>
        <span className="text-xs text-muted-foreground">
          {group.approvals.length}
        </span>
      </div>
      <DataTable
        columns={approvalColumns}
        data={group.approvals}
        manualSorting={false}
        manualPagination={false}
        pagination={false}
        rowClassName={(row) =>
          row.assigned_to_current_user ? "bg-yellow-50" : undefined
        }
        onRowClick={onRowClick}
      />
    </div>
  );
}

function NotificationsCard() {
  const { notifications, unreadCount, isLoading, markAsRead } =
    useNotifications();

  const handleNotificationClick = (notification: SerializedNotification) => {
    if (notification.url) {
      router.visit(notification.url);
    }
  };

  return (
    <Item variant="outline" className="flex-col items-stretch">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Bell className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm font-medium">Notificaciones</span>
        </div>
        {unreadCount > 0 && (
          <Badge variant="secondary" className="text-xs">
            {unreadCount}
          </Badge>
        )}
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-4">
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        </div>
      ) : notifications.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-4">
          No hay notificaciones
        </p>
      ) : (
        <ItemGroup className="mt-2">
          {notifications.slice(0, 5).map((notification, index) => (
            <div key={notification.id}>
              {index > 0 && <ItemSeparator />}
              <Item
                size="sm"
                className={cn(
                  "cursor-pointer",
                  !notification.read_at && "bg-muted/30"
                )}
                onClick={() => handleNotificationClick(notification)}
              >
                <div className="flex w-full items-start justify-between gap-2">
                  <ItemContent>
                    <ItemTitle
                      className={
                        !notification.read_at
                          ? "text-foreground"
                          : "text-muted-foreground"
                      }
                    >
                      {notification.title}
                    </ItemTitle>
                    <ItemDescription className="truncate">
                      {notification.body}
                    </ItemDescription>
                    <span className="text-xs text-muted-foreground/70">
                      {formatDistanceToNow(new Date(notification.created_at), {
                        addSuffix: true,
                        locale: es,
                      })}
                    </span>
                  </ItemContent>
                  {!notification.read_at && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 shrink-0"
                      onClick={(e) => {
                        e.stopPropagation();
                        markAsRead(notification.id);
                      }}
                    >
                      <Check className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </div>
              </Item>
            </div>
          ))}
        </ItemGroup>
      )}

      {notifications.length > 0 && (
        <Button
          variant="ghost"
          size="sm"
          className="w-full mt-2 text-xs"
          onClick={() => router.visit(notificationsPath())}
        >
          Ver todas
        </Button>
      )}
    </Item>
  );
}

export default function GlobalApprovalsIndex({
  grouped_approvals,
  my_requests,
  my_assignments,
  user_roles,
  user_organizations,
}: GlobalApprovalsIndexProps) {
  const help = useHelpDialog("personalHub");

  const handleApprovalRowClick = (row: GlobalApprovalRow) => {
    router.visit(
      `/organizations/${row.organization.slug}/purchase_requests/${row.id}`,
    );
  };

  const handleWorkItemClick = (row: WorkItemRow) => {
    router.visit(
      `/organizations/${row.organization.slug}/purchase_requests/${row.id}`,
    );
  };

  const totalUrgent = grouped_approvals.reduce(
    (acc, g) =>
      acc + g.approvals.filter((a) => a.assigned_to_current_user).length,
    0,
  );

  // Show approvals section if user has approver role OR has pending approvals
  const showApprovalsSection =
    user_roles.has_approver_role || grouped_approvals.length > 0;

  const hasMyWorkItems =
    (user_roles.has_requester_role && my_requests.length > 0) ||
    (user_roles.has_buyer_role && my_assignments.length > 0);

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Hub Central" />
      <div className="flex h-full flex-1 flex-col gap-4 p-4">
        {/* Header */}
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Briefcase className="h-5 w-5 text-muted-foreground" />
            <h1 className="text-2xl font-bold tracking-tight">Hub Central</h1>
            <HelpButton label="Ayuda" variant="outline" onClick={help.open} />
          </div>
          <p className="text-muted-foreground max-w-2xl">
            Acá ves todo lo que necesita tu atención en todas tus organizaciones: solicitudes pendientes de aprobar, las que vos creaste, y las que te asignaron para comprar.
          </p>
        </div>

        {/* Content Grid */}
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          {/* Left Column - Notifications + Organizations + Info */}
          <div className="space-y-4">
            <NotificationsCard />

            <OrganizationsQuickAccess organizations={user_organizations} />

            {totalUrgent > 0 && (
              <Item variant="outline" className="border-yellow-200 bg-yellow-50">
                <HelpCircle className="h-4 w-4 text-yellow-700 shrink-0" />
                <p className="text-sm text-yellow-800">
                  Las filas en{" "}
                  <span className="rounded bg-yellow-200 px-1.5 py-0.5 font-medium">
                    amarillo
                  </span>{" "}
                  requieren tu acción inmediata.
                </p>
              </Item>
            )}
          </div>

          {/* Right/Center - Main Content */}
          <div className="lg:col-span-2 space-y-4">
            {/* Approvals Section - Shown first as it's more urgent */}
            {showApprovalsSection && (
              <Item variant="outline" className="flex-col items-stretch">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Inbox className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm font-medium">
                      Por aprobar
                    </span>
                  </div>
                  {grouped_approvals.reduce(
                    (acc, g) => acc + g.approvals.length,
                    0,
                  ) > 0 && (
                    <Badge variant="secondary" className="text-xs">
                      {grouped_approvals.reduce(
                        (acc, g) => acc + g.approvals.length,
                        0,
                      )}
                    </Badge>
                  )}
                </div>
                <p className="mt-1 text-xs text-muted-foreground">
                  Solicitudes de compra en las que sos aprobador. Las marcadas en amarillo esperan tu decisión ahora.
                </p>

                {grouped_approvals.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12">
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
                      <CheckCircle2 className="h-6 w-6 text-green-600" />
                    </div>
                    <h3 className="mt-3 text-sm font-semibold">
                      Todo al día
                    </h3>
                    <p className="mt-1 text-center text-xs text-muted-foreground">
                      No tenés aprobaciones pendientes.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4 mt-4">
                    {grouped_approvals.map((group) => (
                      <OrganizationApprovals
                        key={group.organization.slug}
                        group={group}
                        onRowClick={handleApprovalRowClick}
                      />
                    ))}
                  </div>
                )}
              </Item>
            )}

            {/* My Work Items (Requests + Assignments consolidated) */}
            {hasMyWorkItems && (
              <MyWorkItemsSection
                myRequests={my_requests}
                myAssignments={my_assignments}
                userRoles={user_roles}
                onRowClick={handleWorkItemClick}
              />
            )}
          </div>
        </div>
      </div>

      <HelpDialog
        open={help.isOpen}
        onOpenChange={help.setIsOpen}
        content={help.content}
      />
    </AppLayout>
  );
}
